import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { useProject } from "@/contexts/ProjectContext";
import ProjectForm from "@/components/ProjectForm";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Building, CalendarDays, CheckCircle, FileText, Folder, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { RibaStage } from "@/types";

const steps = [
  {
    id: "project-details",
    name: "Project Details",
    icon: Folder,
  },
  {
    id: "organisation",
    name: "Organisation",
    icon: Building,
  },
  {
    id: "team",
    name: "Team Members",
    icon: Users,
  },
  {
    id: "documents",
    name: "Initial Documents",
    icon: FileText,
  },
  {
    id: "timeline",
    name: "Timeline",
    icon: CalendarDays,
  },
  {
    id: "review",
    name: "Review & Create",
    icon: CheckCircle,
  }
];

export default function NewProject() {
  const navigate = useNavigate();
  const { createProject } = useProject();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    referenceCode: "",
    client: "",
    location: "",
    budget: "",
    organisationRole: "client",
    team: [],
    documents: [],
    startDate: new Date(),
    targetCompletionDate: new Date(new Date().setFullYear(new Date().getFullYear() + 2)),
  });
  
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const nextStep = () => {
    // Validate current step
    if (currentStep === 0) {
      if (!formData.name || !formData.client) {
        toast({
          title: "Missing information",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }
    }
    
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
      window.scrollTo(0, 0);
    }
  };
  
  const handleSubmit = () => {
    // Create a unique project ID
    const projectId = `proj-${Date.now().toString(36)}`;
    
    // Create RIBA stages with default structure
    const ribaStages: RibaStage[] = Array.from({ length: 8 }, (_, index) => {
      let name, description;
      
      switch (index) {
        case 0:
          name = "Strategic Definition";
          description = "Identify client's Business Case and Strategic Brief and other core project requirements";
          break;
        case 1:
          name = "Preparation and Briefing";
          description = "Develop Project Objectives, including Quality Objectives and Project Outcomes, Sustainability Outcomes";
          break;
        case 2:
          name = "Concept Design";
          description = "Prepare Concept Design, including outline proposals for structural design";
          break;
        case 3:
          name = "Spatial Coordination";
          description = "Prepare Spatial Coordination design in line with Design Programme";
          break;
        case 4:
          name = "Technical Design";
          description = "Prepare Technical Design in accordance with Design Programme";
          break;
        case 5:
          name = "Manufacturing and Construction";
          description = "Offsite manufacturing and onsite Construction in accordance with Construction Programme";
          break;
        case 6:
          name = "Handover";
          description = "Handover of building and conclusion of Building Contract";
          break;
        case 7:
          name = "Use";
          description = "Undertake In Use services in accordance with Schedule of Services";
          break;
        default:
          name = `Stage ${index}`;
          description = "Stage description";
      }
      
      return {
        id: index,
        name,
        description,
        status: index === 0 ? "in-progress" as const : "not-started" as const,
        progress: index === 0 ? 0 : 0,
        activities: [],
        approvals: [],
        dateStarted: index === 0 ? new Date() : undefined,
        informationExchanges: []
      };
    });
    
    // Add default information exchanges
    const infoExchanges = {
      0: [
        { name: "Client Requirements", description: "Document outlining the client's initial requirements", requiredForSignoff: true },
        { name: "Business Case", description: "Business justification for the project", requiredForSignoff: true }
      ],
      1: [
        { name: "Project Brief", description: "Detailed project brief including objectives, sustainability goals, and spatial needs", requiredForSignoff: true },
        { name: "Site Information", description: "Key details and surveys about the project site", requiredForSignoff: true },
        { name: "Project Programme", description: "Key milestones and programmed dates", requiredForSignoff: true },
        { name: "Risk Register", description: "Initial risk assessment and mitigation strategy", requiredForSignoff: true },
        { name: "Project Execution Plan", description: "Plan detailing project management approach", requiredForSignoff: true }
      ]
    };
    
    // Add information exchanges to stages 0 and 1
    ribaStages[0].informationExchanges = infoExchanges[0].map((item, idx) => ({
      id: `exchange-0-${idx}`,
      name: item.name,
      description: item.description,
      status: 'pending' as const,
      requiredForSignoff: item.requiredForSignoff
    }));
    
    ribaStages[1].informationExchanges = infoExchanges[1].map((item, idx) => ({
      id: `exchange-1-${idx}`,
      name: item.name,
      description: item.description,
      status: 'pending' as const,
      requiredForSignoff: item.requiredForSignoff
    }));
    
    // Create the new project object
    const newProject = {
      id: projectId,
      name: formData.name,
      description: formData.description,
      client: formData.client,
      currentStage: 0, // Start at Stage 0: Strategic Definition
      startDate: formData.startDate,
      targetCompletionDate: formData.targetCompletionDate,
      budget: Number(formData.budget) || 0,
      actualCost: 0,
      stages: ribaStages,
      risks: [],
      team: formData.team,
      location: formData.location || "",
      referenceCode: formData.referenceCode || `PROJ-${Math.floor(1000 + Math.random() * 9000)}`,
      status: 'active' as const,
      createdBy: "mock-user",
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    // Create the project in context
    createProject(newProject);
    
    // Navigate to the project dashboard
    toast({
      title: "Project created successfully",
      description: `${formData.name} has been created and started at Stage 0`,
    });
    
    setCurrentProject(newProject);
    navigate("/dashboard");
  };
  
  const setCurrentProject = (project) => {
    const { setCurrentProject } = useProject();
    setCurrentProject(project);
  };
  
  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Project Details
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Project Name <span className="text-red-500">*</span></Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="Enter project name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reference">Project Reference Code</Label>
              <Input 
                id="reference" 
                value={formData.referenceCode} 
                onChange={(e) => handleChange("referenceCode", e.target.value)}
                placeholder="e.g., PROJ-1234 (will be generated if left blank)"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Project Description</Label>
              <Textarea 
                id="description" 
                value={formData.description} 
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Enter a brief description of the project"
                rows={3}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client">Client <span className="text-red-500">*</span></Label>
                <Input 
                  id="client" 
                  value={formData.client} 
                  onChange={(e) => handleChange("client", e.target.value)}
                  placeholder="Client name or organization"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input 
                  id="location" 
                  value={formData.location} 
                  onChange={(e) => handleChange("location", e.target.value)}
                  placeholder="Project location"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="budget">Budget (£)</Label>
                <Input 
                  id="budget" 
                  value={formData.budget} 
                  onChange={(e) => handleChange("budget", e.target.value)}
                  placeholder="Enter budget amount"
                  type="number"
                />
              </div>
            </div>
          </div>
        );
      
      case 1: // Organisation
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="orgRole">Organisation Role</Label>
              <Select
                value={formData.organisationRole}
                onValueChange={(value) => handleChange("organisationRole", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role in project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="designer">Designer</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                This will help determine applicable activities and information requirements
              </p>
            </div>
            
            {/* Additional organisation settings would go here */}
            <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
              <h3 className="font-medium text-blue-800">Organisation Role: {formData.organisationRole.charAt(0).toUpperCase() + formData.organisationRole.slice(1)}</h3>
              <p className="text-sm text-blue-700 mt-1">
                {formData.organisationRole === 'client' && "As the client, you'll manage project requirements, approvals, and overall governance."}
                {formData.organisationRole === 'designer' && "As the designer, you'll manage design development, specifications, and technical documents."}
                {formData.organisationRole === 'contractor' && "As the contractor, you'll manage construction activities, procurement, and site operations."}
                {formData.organisationRole === 'consultant' && "As a consultant, you'll provide specialist expertise and advisement to the project team."}
              </p>
            </div>
          </div>
        );
        
      case 2: // Team Members
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Add team members who will be working on this project. You can also add team members later.
            </p>
            
            <div className="bg-gray-50 p-4 rounded-md text-center">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <h3 className="text-lg font-medium mb-1">No team members added yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Team members will have access to project information based on their roles
              </p>
              <Button variant="outline">
                Add Team Member
              </Button>
            </div>
          </div>
        );
        
      case 3: // Initial Documents
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Upload foundational documents for your project. These will help set up initial Information Exchanges.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {["Client Requirements", "Business Case", "Site Information", "Project Execution Plan"].map((docName, idx) => (
                <div key={idx} className="border rounded-md p-4">
                  <h3 className="font-medium mb-2">{docName}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Required for RIBA Stage {idx < 2 ? '0' : '1'}
                  </p>
                  <Button variant="outline" className="w-full">Upload Document</Button>
                </div>
              ))}
            </div>
            
            <p className="text-sm text-muted-foreground">
              You can also upload these documents later from the Information Exchange section.
            </p>
          </div>
        );
        
      case 4: // Timeline
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">Project Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate.toISOString().split('T')[0]}
                  onChange={(e) => handleChange("startDate", new Date(e.target.value))}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="endDate">Target Completion Date</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.targetCompletionDate.toISOString().split('T')[0]}
                  onChange={(e) => handleChange("targetCompletionDate", new Date(e.target.value))}
                />
              </div>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium mb-2">RIBA Stage Duration Guidance</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Based on your project details, here's a recommended timeline for each RIBA stage:
              </p>
              
              <div className="space-y-3">
                {[
                  { stage: 0, name: "Strategic Definition", time: "2-4 weeks" },
                  { stage: 1, name: "Preparation and Briefing", time: "4-6 weeks" },
                  { stage: 2, name: "Concept Design", time: "8-12 weeks" },
                  { stage: 3, name: "Spatial Coordination", time: "8-12 weeks" },
                  { stage: 4, name: "Technical Design", time: "12-16 weeks" },
                  { stage: 5, name: "Manufacturing and Construction", time: "Variable" },
                  { stage: 6, name: "Handover", time: "2-4 weeks" },
                  { stage: 7, name: "Use", time: "Ongoing" }
                ].map((item) => (
                  <div key={item.stage} className="flex justify-between items-center">
                    <span className="text-sm">Stage {item.stage}: {item.name}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">{item.time}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-muted-foreground mt-4">
                These are general guidelines. You'll be able to set specific dates for each stage later.
              </p>
            </div>
          </div>
        );
        
      case 5: // Review & Create
        return (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
              <h3 className="text-lg font-medium text-green-800 flex items-center">
                <CheckCircle className="mr-2 h-5 w-5" /> Ready to Create
              </h3>
              <p className="text-green-700 mt-1">
                Your project setup is ready. Review the details below before creating.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-medium border-b pb-1 mb-2">Project Details</h3>
                <dl className="space-y-1">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Name:</dt>
                    <dd className="font-medium">{formData.name}</dd>
                  </div>
                  {formData.referenceCode && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Reference:</dt>
                      <dd className="font-medium">{formData.referenceCode}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Client:</dt>
                    <dd className="font-medium">{formData.client}</dd>
                  </div>
                  {formData.location && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Location:</dt>
                      <dd className="font-medium">{formData.location}</dd>
                    </div>
                  )}
                  {formData.budget && (
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">Budget:</dt>
                      <dd className="font-medium">£{Number(formData.budget).toLocaleString()}</dd>
                    </div>
                  )}
                </dl>
              </div>
              
              <div>
                <h3 className="font-medium border-b pb-1 mb-2">Timeline & Organisation</h3>
                <dl className="space-y-1">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Start Date:</dt>
                    <dd className="font-medium">{formData.startDate.toLocaleDateString()}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Target Completion:</dt>
                    <dd className="font-medium">{formData.targetCompletionDate.toLocaleDateString()}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Organisation Role:</dt>
                    <dd className="font-medium capitalize">{formData.organisationRole}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Team Members:</dt>
                    <dd className="font-medium">{formData.team.length || "None (can add later)"}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Initial Documents:</dt>
                    <dd className="font-medium">{formData.documents.length || "None (can add later)"}</dd>
                  </div>
                </dl>
              </div>
            </div>
            
            <div className="mt-6">
              <p className="text-sm text-muted-foreground mb-2">
                Your project will start at <strong>Stage 0: Strategic Definition</strong> with the required Information Exchanges.
              </p>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <main className="flex-1 container py-6">
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          
          <h1 className="text-3xl font-bold mt-4">Create New Project</h1>
          <p className="text-muted-foreground">
            Create a new RIBA-aligned project using the guided setup wizard below.
          </p>
        </div>
        
        {/* Step indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between w-full">
            {steps.map((step, idx) => (
              <React.Fragment key={step.id}>
                {idx > 0 && (
                  <div className={`flex-1 h-1 ${idx <= currentStep ? 'bg-primary' : 'bg-gray-200'}`}></div>
                )}
                <div className="relative">
                  <div 
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center border-2 
                      ${idx < currentStep ? 'bg-primary border-primary' : 
                        idx === currentStep ? 'bg-white border-primary' : 
                        'bg-white border-gray-300'}
                    `}
                  >
                    {idx < currentStep ? (
                      <CheckCircle className="h-5 w-5 text-white" />
                    ) : (
                      <step.icon className={`h-5 w-5 ${idx === currentStep ? 'text-primary' : 'text-gray-400'}`} />
                    )}
                  </div>
                  <div className={`
                    absolute top-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap
                    text-xs font-medium
                    ${idx === currentStep ? 'text-primary' : 'text-gray-500'}
                  `}>
                    {step.name}
                  </div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
        
        <Card>
          <CardContent className="pt-6">
            {/* Wrap TabsContent within a Tabs component */}
            <Tabs value={steps[currentStep].id}>
              <TabsContent value={steps[currentStep].id} className="mt-0">
                {renderStepContent()}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Previous
          </Button>
          
          {currentStep < steps.length - 1 ? (
            <Button onClick={nextStep}>
              Next <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleSubmit}>
              Create Project <CheckCircle className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
